import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import TopBar from '../components/TopBar';
import FormField from '../components/FormField';
import layoutStyles from '../styles/layout.module.css';
import styles from './TypedCreate.module.css';
import { UserContext } from '../App';
import { db, functions, storage } from '../firebase';
import { deriveActiveConnectStatus } from '../utils/stripeConnect';
import { getRoleAccessState, ROLE_ARTIST } from '../utils/roles';
import { createSquareCroppedImage } from '../utils/imageCrop';
import { createVersionToken, useStorageUrl } from '../utils/storageMedia';

const PROFILE_ORIGINAL_MAX_SIZE = 2200;
const PROFILE_IMAGE_SIZE = 1000;
const PROFILE_THUMB_SIZE = 200;
const PROFILE_TINY_SIZE = 50;
const SIGNATURE_ORIGINAL_MAX_SIZE = 1600;
const SIGNATURE_IMAGE_SIZE = 1000;
const CONNECT_COUNTRIES = [
  { code: 'CA', label: 'Canada', enabled: true },
  { code: 'US', label: 'United States', enabled: false },
];

function centeredCropForImage(image) {
  const side = Math.min(image.width, image.height);
  return {
    x: Math.floor((image.width - side) / 2),
    y: Math.floor((image.height - side) / 2),
    width: side,
    height: side,
  };
}

async function createSquareImageSet(file, variants = []) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const crop = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(centeredCropForImage(image));
      image.onerror = () => reject(new Error('Invalid image file.'));
      image.src = objectUrl;
    });
    return Promise.all(
      variants.map(({ size, fileName }) => createSquareCroppedImage({
        file,
        crop,
        size,
        mimeType: 'image/jpeg',
        fileName,
      }))
    );
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function createArtistProfileImages(file) {
  return createSquareImageSet(file, [
    { size: PROFILE_ORIGINAL_MAX_SIZE, fileName: 'artist-profile-original.jpg' },
    { size: PROFILE_IMAGE_SIZE, fileName: 'artist-profile-1000.jpg' },
    { size: PROFILE_THUMB_SIZE, fileName: 'artist-profile-200.jpg' },
    { size: PROFILE_TINY_SIZE, fileName: 'artist-profile-50.jpg' },
  ]);
}

async function createArtistSignatureImages(file) {
  return createSquareImageSet(file, [
    { size: SIGNATURE_ORIGINAL_MAX_SIZE, fileName: 'artist-signature-original.jpg' },
    { size: SIGNATURE_IMAGE_SIZE, fileName: 'artist-signature-1000.jpg' },
  ]);
}

export default function UpdateArtist() {
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const artistAccess = getRoleAccessState(appUser, ROLE_ARTIST);
  const connectStatus = deriveActiveConnectStatus(appUser);
  const hasStripeAccount = Boolean(connectStatus.accountId);
  const savedConnectCountry = String(appUser?.settings?.country || '').toUpperCase();
  const [form, setForm] = useState({
    artistName: '',
    description: '',
    instagram: '',
    twitter: '',
    tiktok: '',
    youtube: '',
    website: '',
  });
  const [existingPhoto, setExistingPhoto] = useState({
    originalUrl: '',
    originalPath: '',
    url: '',
    thumbUrl: '',
    tinyUrl: '',
    path: '',
    thumbPath: '',
    tinyPath: '',
  });
  const [existingSignature, setExistingSignature] = useState({
    originalPath: '',
    path: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [connectCountry, setConnectCountry] = useState('');

  useEffect(() => () => {
    if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  useEffect(() => () => {
    if (signaturePreview?.startsWith('blob:')) URL.revokeObjectURL(signaturePreview);
  }, [signaturePreview]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!appUser?.artistId) {
        if (active) {
          setForm((prev) => ({ ...prev, artistName: [appUser?.firstName, appUser?.lastName].filter(Boolean).join(' ').trim() }));
          setLoading(false);
        }
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'artists', appUser.artistId));
        if (!active) return;
        if (snap.exists()) {
          const data = snap.data() || {};
          setForm({
            artistName: data.artistFullName || '',
            description: data.artistDescription || '',
            instagram: data.instagram || '',
            twitter: data.twitter || '',
            tiktok: data.tiktok || '',
            youtube: data.youtube || '',
            website: data.website || '',
          });
          setExistingPhoto({
            originalPath: data.artistProfilePhotoOriginalPath || '',
            path: data.artistProfilePhotoPath || '',
            thumbPath: data.artistProfilePhotoThumbPath || '',
            tinyPath: data.artistProfilePhoto50Path || '',
          });
          setExistingSignature({
            originalPath: data.artistSignatureOriginalPath || '',
            path: data.artistSignaturePath || data.artistSignature1000Path || '',
          });
        }
      } catch (err) {
        if (active) setError(err?.message || 'Unable to load artist profile.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [appUser?.artistId, appUser?.firstName, appUser?.lastName]);

  useEffect(() => {
    if (!appUser?.artistId && !hasStripeAccount) {
      setConnectModalOpen(true);
    } else {
      setConnectModalOpen(false);
    }
  }, [appUser?.artistId, hasStripeAccount]);

  useEffect(() => {
    if (!connectModalOpen) {
      setConnectCountry('');
      setConnectError('');
      return;
    }
    const preferred = savedConnectCountry === 'CA' ? savedConnectCountry : 'CA';
    setConnectCountry((current) => current || preferred);
  }, [connectModalOpen, savedConnectCountry]);

  useEffect(() => {
    if (location.search.includes('stripe_connect_return=1') && hasStripeAccount) {
      navigate('/creator/profile', { replace: true });
    }
  }, [hasStripeAccount, location.search, navigate]);

  const existingThumbUrl = useStorageUrl(existingPhoto.thumbPath || existingPhoto.path);
  const existingSignatureUrl = useStorageUrl(existingSignature.path);
  const currentPreview = useMemo(() => photoPreview || existingThumbUrl || '', [existingThumbUrl, photoPreview]);
  const currentSignaturePreview = useMemo(() => signaturePreview || existingSignatureUrl || '', [existingSignatureUrl, signaturePreview]);

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file.');
      return;
    }
    setError('');
    setPhotoFile(file);
    if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSignatureChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file for the signature.');
      return;
    }
    setError('');
    setSignatureFile(file);
    if (signaturePreview?.startsWith('blob:')) URL.revokeObjectURL(signaturePreview);
    setSignaturePreview(URL.createObjectURL(file));
  };

  const uploadArtistPhoto = async (artistId) => {
    if (!photoFile) return existingPhoto;
    const [originalFile, largeFile, thumbFile, tinyFile] = await createArtistProfileImages(photoFile);
    const versionToken = createVersionToken();
    const photoOriginalPath = `artists/${artistId}/profile-original-v${versionToken}.jpg`;
    const photoPath = `artists/${artistId}/profile-1000-v${versionToken}.jpg`;
    const photoThumbPath = `artists/${artistId}/profile-200-v${versionToken}.jpg`;
    const photoTinyPath = `artists/${artistId}/profile-50-v${versionToken}.jpg`;
    const originalRef = ref(storage, photoOriginalPath);
    const largeRef = ref(storage, photoPath);
    const thumbRef = ref(storage, photoThumbPath);
    const tinyRef = ref(storage, photoTinyPath);
    const originalTask = uploadBytesResumable(originalRef, originalFile, {
      contentType: originalFile.type || 'image/jpeg',
    });
    const largeTask = uploadBytesResumable(largeRef, largeFile, {
      contentType: largeFile.type || 'image/jpeg',
    });
    const thumbTask = uploadBytesResumable(thumbRef, thumbFile, {
      contentType: thumbFile.type || 'image/jpeg',
    });
    const tinyTask = uploadBytesResumable(tinyRef, tinyFile, {
      contentType: tinyFile.type || 'image/jpeg',
    });

    await Promise.all([
      new Promise((resolve, reject) => {
        originalTask.on('state_changed', () => {}, reject, resolve);
      }),
      new Promise((resolve, reject) => {
        largeTask.on('state_changed', () => {}, reject, resolve);
      }),
      new Promise((resolve, reject) => {
        thumbTask.on('state_changed', () => {}, reject, resolve);
      }),
      new Promise((resolve, reject) => {
        tinyTask.on('state_changed', () => {}, reject, resolve);
      }),
    ]);

    return {
      originalPath: photoOriginalPath,
      path: photoPath,
      thumbPath: photoThumbPath,
      tinyPath: photoTinyPath,
    };
  };

  const uploadArtistSignature = async (artistId) => {
    if (!signatureFile) return existingSignature;
    const [originalFile, displayFile] = await createArtistSignatureImages(signatureFile);
    const versionToken = createVersionToken();
    const signatureOriginalPath = `artists/${artistId}/signature-original-v${versionToken}.jpg`;
    const signaturePath = `artists/${artistId}/signature-1000-v${versionToken}.jpg`;
    const originalRef = ref(storage, signatureOriginalPath);
    const displayRef = ref(storage, signaturePath);
    const originalTask = uploadBytesResumable(originalRef, originalFile, {
      contentType: originalFile.type || 'image/jpeg',
    });
    const displayTask = uploadBytesResumable(displayRef, displayFile, {
      contentType: displayFile.type || 'image/jpeg',
    });

    await Promise.all([
      new Promise((resolve, reject) => {
        originalTask.on('state_changed', () => {}, reject, resolve);
      }),
      new Promise((resolve, reject) => {
        displayTask.on('state_changed', () => {}, reject, resolve);
      }),
    ]);

    return {
      originalPath: signatureOriginalPath,
      path: signaturePath,
    };
  };

  const startStripeSetup = useCallback(async () => {
    try {
      if (!connectCountry) {
        throw new Error('Choose the country for your Stripe Connect account before continuing.');
      }
      setConnectBusy(true);
      setConnectError('');
      await setDoc(doc(db, 'users', appUser.id), {
        settings: {
          ...(appUser?.settings || {}),
          country: connectCountry,
        },
        updatedAt: serverTimestamp(),
      }, { merge: true });
      const baseUrl = window.location.origin;
      const callable = httpsCallable(functions, 'createStripeExpressAccountLink');
      const result = await callable({
        country: connectCountry,
        refreshUrl: `${baseUrl}/creator/profile`,
        returnUrl: `${baseUrl}/creator/profile?stripe_connect_return=1`,
      });
      const url = result?.data?.url;
      if (!url) throw new Error('Unable to create Stripe Connect account.');
      window.location.assign(url);
    } catch (err) {
      setConnectError(err?.message || 'Unable to start Stripe setup.');
      setConnectBusy(false);
    }
  }, [appUser?.id, appUser?.settings, connectCountry]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!artistAccess.isActive) {
      setError('An active Artist subscription is required.');
      return;
    }
    if (!appUser?.artistId && !photoFile) {
      setError('Upload an artist photo.');
      return;
    }
    if (!appUser?.artistId && !signatureFile) {
      setError('Upload a signature image.');
      return;
    }
    if (!appUser?.artistId && !hasStripeAccount) {
      setError('Create your Stripe Connect account before creating an artist profile.');
      setConnectModalOpen(true);
      return;
    }
    try {
      setSaving(true);
      setError('');
      const nextArtistId = appUser?.artistId || `artist-${appUser?.id || Date.now()}`;
      const uploadedPhoto = await uploadArtistPhoto(nextArtistId);
      const uploadedSignature = await uploadArtistSignature(nextArtistId);
      const callable = httpsCallable(functions, appUser?.artistId ? 'updateArtistProfile' : 'createArtistProfile');
      const payload = appUser?.artistId
        ? {
          artistId: appUser.artistId,
          input: {
            ...form,
            photoOriginalPath: uploadedPhoto.originalPath,
            photoPath: uploadedPhoto.path,
            photoThumbPath: uploadedPhoto.thumbPath,
            photo50Path: uploadedPhoto.tinyPath,
            signatureOriginalPath: uploadedSignature.originalPath,
            signaturePath: uploadedSignature.path,
          },
        }
        : {
          input: {
            ...form,
            photoOriginalPath: uploadedPhoto.originalPath,
            photoPath: uploadedPhoto.path,
            photoThumbPath: uploadedPhoto.thumbPath,
            photo50Path: uploadedPhoto.tinyPath,
            signatureOriginalPath: uploadedSignature.originalPath,
            signaturePath: uploadedSignature.path,
          },
        };
      const response = await callable(payload);
      const createdArtistId = response?.data?.artistId || appUser?.artistId;
      navigate(`/artists/${createdArtistId}`, { replace: true, state: { fromCreator: true } });
    } catch (err) {
      setError(err?.message || 'Unable to save artist profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => navigate('/creator')} />
      <div className={`${layoutStyles.detailMain} ${styles.page}`.trim()}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Creator</span>
          <h1>{appUser?.artistId ? 'Edit Artist Profile' : 'Create Artist Profile'}</h1>
          <p>Artist profile media uploads directly into Firebase Storage. Artist photos save multiple square variants, and signatures save the square back-face artwork for the collectible coin.</p>
        </header>

        {loading ? <p className={styles.notice}>Loading artist profile…</p> : null}
        {!artistAccess.isActive ? <p className={styles.notice}>Artist access is expired. Reactivate it in Payment before saving changes.</p> : null}
        {!appUser?.artistId && !hasStripeAccount ? <p className={styles.notice}>Set up your Stripe Connect account before creating your artist profile.</p> : null}

        <form className={styles.form} onSubmit={handleSubmit}>
          <FormField label="Artist Name">
            <input value={form.artistName} onChange={(event) => setForm((prev) => ({ ...prev, artistName: event.target.value }))} required />
          </FormField>
          <FormField label="Description">
            <textarea value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} rows={5} />
          </FormField>
          <FormField label="Artist Photo" helper="Uploads to Firebase Storage and saves original, 1000, 200, and 50 pixel square variants.">
            <input type="file" accept="image/*" onChange={handlePhotoChange} />
            {currentPreview ? <img src={currentPreview} alt="Artist preview" style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 24, marginTop: 12 }} /> : null}
          </FormField>
          <FormField label="Signature" helper="Upload a square signature image for the back of the coin. Required when creating a new artist profile.">
            <input type="file" accept="image/*" onChange={handleSignatureChange} />
            {currentSignaturePreview ? <img src={currentSignaturePreview} alt="Signature preview" style={{ width: 180, height: 180, objectFit: 'cover', borderRadius: 24, marginTop: 12 }} /> : null}
          </FormField>
          <div className={styles.grid}>
            <FormField label="Instagram">
              <input value={form.instagram} onChange={(event) => setForm((prev) => ({ ...prev, instagram: event.target.value }))} />
            </FormField>
            <FormField label="Twitter / X">
              <input value={form.twitter} onChange={(event) => setForm((prev) => ({ ...prev, twitter: event.target.value }))} />
            </FormField>
            <FormField label="TikTok">
              <input value={form.tiktok} onChange={(event) => setForm((prev) => ({ ...prev, tiktok: event.target.value }))} />
            </FormField>
            <FormField label="YouTube">
              <input value={form.youtube} onChange={(event) => setForm((prev) => ({ ...prev, youtube: event.target.value }))} />
            </FormField>
          </div>
          <FormField label="Website">
            <input value={form.website} onChange={(event) => setForm((prev) => ({ ...prev, website: event.target.value }))} />
          </FormField>
          {error ? <p className={styles.error}>{error}</p> : null}
          <button type="submit" className={styles.primaryButton} disabled={saving || !artistAccess.isActive}>{saving ? 'Saving…' : 'Save Artist Profile'}</button>
        </form>

        {connectModalOpen ? (
          <div className={styles.modalBackdrop} role="presentation">
            <div className={styles.modalCard} role="dialog" aria-modal="true" aria-labelledby="stripeSetupHeading">
              <span className={styles.eyebrow}>Stripe Setup</span>
              <h2 id="stripeSetupHeading">Create your Stripe payout account first</h2>
              <p className={styles.modalCopy}>Choose Canada for your Stripe Connect account before creating your artist profile. The United States option stays visible in the dropdown but is currently unavailable. This country cannot be changed without resetting Stripe later.</p>
              {savedConnectCountry ? <p className={styles.readOnlyBox}>Saved choice: {savedConnectCountry}</p> : null}
              <FormField label="Stripe Account Country">
                <select value={connectCountry} onChange={(event) => setConnectCountry(event.target.value)} disabled={connectBusy}>
                  <option value="" disabled>Select country</option>
                  {CONNECT_COUNTRIES.map((entry) => (
                    <option key={entry.code} value={entry.code} disabled={!entry.enabled}>{entry.label}{entry.enabled ? '' : ' (Unavailable)'}</option>
                  ))}
                </select>
              </FormField>
              {connectError ? <p className={styles.error}>{connectError}</p> : null}
              <div className={styles.modalActions}>
                <button type="button" className={styles.secondaryButton} onClick={() => navigate('/creator')} disabled={connectBusy}>Back</button>
                <button type="button" className={styles.primaryButton} onClick={startStripeSetup} disabled={connectBusy || !connectCountry}>
                  {connectBusy ? 'Starting…' : 'Create Stripe Account'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
